import { PATHS } from "@constants/Paths";
import { parseToken, PolicyContextProvider, setToken, useAnalytics, } from "@piiqtechnologies/ui-lib";
import * as Sentry from "@sentry/react";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EVENTS } from "@analytics-setup/events";
import { generateAllowedActions, getIsDemoUser, getIsDiscoverAdmin, getIsViewerUser, getRedirectPathAfterLogin, getUserContext, storeUserContext, } from "./utils";
import { clearEnterpriseCache } from "../hooks/useIsEnterprise";
export const AuthContext = createContext(null);
// Flag to track if we've already handled the demo auto-login session clear
let hasHandledDemoAutoLogin = false;
// Check if we're on the demo page with free report params - if so, start fresh
const getInitialUserContext = () => {
    const isOnDemoPage = window.location.pathname.includes("/demo");
    const searchParams = new URLSearchParams(window.location.search);
    const hasAutoLoginParams = searchParams.has("email") && searchParams.has("communityName");
    // If coming from free report flow to demo, clear session and start fresh (only once)
    if (isOnDemoPage && hasAutoLoginParams && !hasHandledDemoAutoLogin) {
        hasHandledDemoAutoLogin = true;
        setToken(null);
        sessionStorage.clear();
        return null;
    }
    return getUserContext();
};
const getAssessmentIdFromPath = (pathname) => {
    const match = pathname.match(/\/overview\/([^/?]+)/i);
    return match === null || match === void 0 ? void 0 : match[1];
};
const AuthContextProvider = ({ children }) => {
    const [userContext, setUserContext] = useState(getInitialUserContext());
    const [shouldNavigate, setShouldNavigate] = useState(false);
    const [showSetPasswordBanner, setShowSetPasswordBanner] = useState(false);
    const analytics = useAnalytics();
    const navigate = useNavigate();
    const location = useLocation();
    const { state: locationState } = location;
    const redirectPathAfterLogin = useMemo(() => getRedirectPathAfterLogin(userContext === null || userContext === void 0 ? void 0 : userContext.user), [userContext === null || userContext === void 0 ? void 0 : userContext.user]);
    const normalizeUserFromToken = useCallback((tokenPayload) => {
        var _a, _b;
        if (!(tokenPayload === null || tokenPayload === void 0 ? void 0 : tokenPayload._id))
            return undefined;
        // Handle both 'organizations' and 'discoverOrganizations' from token
        const organizations = (((_a = tokenPayload.organizations) === null || _a === void 0 ? void 0 : _a.length) ? tokenPayload.organizations : undefined) ||
            ((_b = tokenPayload.discoverOrganizations) === null || _b === void 0 ? void 0 : _b.map((org) => (typeof org === "string" ? { _id: org, name: "" } : org))) ||
            [];
        return {
            _id: tokenPayload._id,
            status: tokenPayload.status || "active",
            email: tokenPayload.email || "",
            name: tokenPayload.name || "",
            receiveEmailDigest: Boolean(tokenPayload.receiveEmailDigest),
            scanNotificationEmail: Boolean(tokenPayload.scanNotificationEmail),
            organization: organizations[0],
            organizations,
            communities: tokenPayload.communities,
            allow: tokenPayload.allow,
            deny: tokenPayload.deny,
            features: tokenPayload.features,
            roleAlias: tokenPayload.roleAlias,
            roleId: tokenPayload.roleId,
            discoverRole: tokenPayload.discoverRole,
            discoverRoleAlias: tokenPayload.discoverRoleAlias,
            termsAndConditionsAcceptedAt: tokenPayload.termsAndConditionsAcceptedAt,
        };
    }, []);
    const onLogin = useCallback((loggedInUser, sso, options) => {
        const { token } = loggedInUser;
        try {
            const tokenPayload = parseToken(token);
            const user = normalizeUserFromToken(tokenPayload);
            if (!user) {
                throw new Error("Invalid token payload");
            }
            if (!(options === null || options === void 0 ? void 0 : options.skipRedirect)) {
                setShouldNavigate(true);
            }
            setToken(token);
            setUserContext((_userContext) => {
                const newContext = Object.assign(Object.assign({}, _userContext), { sso, user: user || (_userContext === null || _userContext === void 0 ? void 0 : _userContext.user), isFreeTrial: Boolean(_userContext === null || _userContext === void 0 ? void 0 : _userContext.isFreeTrial) });
                storeUserContext(newContext);
                return newContext;
            });
            analytics.track(EVENTS.auth.loginSucceeded, { method: sso ? "sso" : "password" });
        }
        catch (error) {
            console.error("Invalid login token", error);
            analytics.track(EVENTS.auth.loginFailed, {
                method: sso ? "sso" : "password",
                reason: "invalid_token",
            });
            setShouldNavigate(false);
            const assessmentId = getAssessmentIdFromPath(location.pathname);
            const invalidTokenState = assessmentId ? { assessmentId } : undefined;
            navigate(PATHS.INVALID_TOKEN, { replace: true, state: invalidTokenState });
        }
    }, [normalizeUserFromToken, navigate, location.pathname, analytics]);
    useEffect(() => {
        var _a, _b, _c;
        if ((_a = userContext === null || userContext === void 0 ? void 0 : userContext.user) === null || _a === void 0 ? void 0 : _a._id) {
            const user = userContext.user;
            const isDiscoverAdmin = getIsDiscoverAdmin(user);
            const isDemoUser = getIsDemoUser(user);
            const isViewerUser = getIsViewerUser(user);
            // Canonical DiscoverRoleAlias values only; leave unset when the user has no discover role.
            const role = isDiscoverAdmin
                ? "discover_admin"
                : isDemoUser
                    ? "discover_demo"
                    : isViewerUser
                        ? "discover_viewer"
                        : undefined;
            analytics.identify(user._id, {
                name: user.name,
                email: user.email,
                organization: (_b = user.organization) === null || _b === void 0 ? void 0 : _b.name,
                organizationId: (_c = user.organization) === null || _c === void 0 ? void 0 : _c._id,
                role,
                isDemoUser,
                isViewerUser,
                isDiscoverAdmin,
                isFreeTrial: Boolean(userContext === null || userContext === void 0 ? void 0 : userContext.isFreeTrial),
            });
            Sentry.setUser({
                id: user._id,
            });
        }
    }, [userContext]);
    useEffect(() => {
        if (!(userContext === null || userContext === void 0 ? void 0 : userContext.user))
            return;
        // A `redirectTo` carried on the auth callback URL (the embed signup hand-off
        // /login?token=...&redirectTo=/report-type) takes precedence, keeping the
        // post-login destination centralised here rather than in the calling page.
        // Restricted to same-origin internal paths to avoid an open redirect.
        const redirectToParam = new URLSearchParams(location.search).get("redirectTo");
        const safeRedirectTo = redirectToParam && redirectToParam.startsWith("/") && !redirectToParam.startsWith("//") ? redirectToParam : null;
        let redirectPath = safeRedirectTo || redirectPathAfterLogin;
        if (locationState && !safeRedirectTo) {
            redirectPath = PATHS.HOME;
        }
        shouldNavigate && navigate(redirectPath, { replace: true });
        setShouldNavigate(false);
    }, [userContext === null || userContext === void 0 ? void 0 : userContext.user, redirectPathAfterLogin, locationState, location.search, navigate, shouldNavigate]);
    const onLogout = () => {
        // Track before clearing so the event is still associated with the user.
        analytics.track(EVENTS.auth.loggedOut);
        setToken(null);
        sessionStorage.clear();
        setUserContext(null);
        clearEnterpriseCache();
        Sentry.configureScope((scope) => {
            scope.setUser(null);
        });
    };
    const setFreeTrial = useCallback(() => {
        setUserContext((prevContext) => {
            const newContext = Object.assign(Object.assign({}, prevContext), { isFreeTrial: true });
            storeUserContext(newContext);
            return newContext;
        });
    }, []);
    const syncUserContext = (updates) => {
        setUserContext((prevContext) => {
            if (!(prevContext === null || prevContext === void 0 ? void 0 : prevContext.user))
                return prevContext;
            const newContext = Object.assign(Object.assign({}, prevContext), { user: Object.assign(Object.assign({}, prevContext.user), updates) });
            storeUserContext(newContext);
            return newContext;
        });
    };
    const user = userContext === null || userContext === void 0 ? void 0 : userContext.user;
    const allowedActions = useMemo(() => generateAllowedActions({ user }), [user]);
    return (React.createElement(AuthContext.Provider, { value: {
            isFreeTrial: Boolean(userContext === null || userContext === void 0 ? void 0 : userContext.isFreeTrial),
            user: userContext === null || userContext === void 0 ? void 0 : userContext.user,
            sso: userContext === null || userContext === void 0 ? void 0 : userContext.sso,
            onLogin,
            onLogout,
            syncUserContext,
            setFreeTrial,
            showSetPasswordBanner,
            setShowSetPasswordBanner,
        } },
        React.createElement(PolicyContextProvider, { allow: allowedActions, deny: [] }, children)));
};
export function useAuthContext() {
    return useContext(AuthContext);
}
export default AuthContextProvider;
